import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  AlertDialog,
  PageHeader,
  PageHeaderSkeleton,
  Stack,
  TabBar,
  type TabBarItem,
  useDelayedLoading,
} from '@leanin/ui';
import { type MembershipRequestView } from '../../../lib/queries/groups';
import {
  useApproveMembershipRequest,
  useDeclineMembershipRequest,
} from '../../../lib/mutations/groups';
import { trackIntakeRequestDecided } from '../../../lib/analytics/events';
import { useMessageMember } from '../../groups/use-message-member';
import { InlineStatus } from '../../InlineStatus';
import { AdminJoinRequestsCard, type ReviewTab } from './AdminJoinRequestsCard';
import { AdminJoinRequestsCardSkeleton } from './AdminJoinRequestsCard.skeleton';
import { useErrorMessage } from '../../../lib/error-message';
import { decisionErrorMessage } from './request-decision-error-copy';
import { ViewApplicationModal } from './ViewApplicationModal';
import {
  useLeaderRequestsByStatus,
  useReviewableJoinRequests,
} from './use-reviewable-join-requests';

// Awaiting-review / Accepted / Declined join requests for circles and networks.
// (Spaces don't have request-to-join.) The Awaiting-review tab is the leader's
// action list — approve / decline; Accepted and Declined are history, with a
// reverse-to-approve on the Declined tab (D3). Any row that carries an intake
// snapshot exposes a "View application" affordance (ViewApplicationModal).
//
// The status filter is the platform-canonical `TabBar` count-pill strip (same
// as every other filtered list surface), not a bespoke control.

type Props = {
  readonly kind: 'circle' | 'network';
  readonly groupId: string;
};

export function GroupRequestsSection({ kind, groupId }: Props) {
  const t = useTranslations('Group');
  const describeError = useErrorMessage();
  const navigate = useNavigate();
  const [tab, setTab] = useState<ReviewTab>('pending');
  const [viewing, setViewing] = useState<MembershipRequestView | null>(null);
  // Declining is destructive (the applicant is notified their request wasn't
  // approved), so it goes through a confirmation — matching the Overview
  // join-requests card. Both the list card and the review modal route their
  // decline through `handleDecline`, so the confirm covers both surfaces.
  const [declineTarget, setDeclineTarget] =
    useState<MembershipRequestView | null>(null);

  // All three slices are fetched so the filter's per-status counts stay live
  // and switching tabs is instant. Each already excludes the viewer's own
  // request; the pending set also feeds the sidebar badge, so count and list
  // can't drift.
  const pending = useReviewableJoinRequests(kind, groupId);
  const accepted = useLeaderRequestsByStatus(kind, groupId, 'accepted');
  const declined = useLeaderRequestsByStatus(kind, groupId, 'declined');

  const approve = useApproveMembershipRequest();
  const decline = useDeclineMembershipRequest();
  const { messageMember, error: messageError } = useMessageMember();
  // Scoped to the request the leader acted on, so the card renders it on that
  // row and the review modal renders it in place — never off-screen.
  const [decisionError, setDecisionError] = useState<{
    requestId: string;
    message: string;
  } | null>(null);
  const isPending = approve.isPending || decline.isPending;

  const rows =
    tab === 'pending'
      ? pending.requests
      : tab === 'accepted'
        ? accepted.requests
        : declined.requests;
  const isLoading =
    tab === 'pending'
      ? pending.isLoading
      : tab === 'accepted'
        ? accepted.isLoading
        : declined.isLoading;

  const handleApprove = (requestId: string, onApproved?: () => void) => {
    setDecisionError(null);
    // A circle-applicant approval re-parents the Circle onto this Network, so
    // thread the Circle id through for the circle-network-link cache refresh.
    const row = rows.find((r) => r.id === requestId);
    const subjectCircleId =
      row?.applicantType === 'circle' ? (row.subjectCircle?.id ?? undefined) : undefined;
    // Approving a request that is currently in the Declined tab is a reversal
    // (D3 reverse-to-approve); from Awaiting review it is a fresh approval.
    const outcome = tab === 'declined' ? 'reversed' : 'approved';
    approve.mutate(
      { groupId, requestId, kind, ...(subjectCircleId ? { subjectCircleId } : {}) },
      {
        onSuccess: () => {
          onApproved?.();
          if (row) {
            trackIntakeRequestDecided({
              // undefined applicantType = a legacy user-subject request.
              applicant_type: row.applicantType ?? 'user',
              destination_type: kind,
              outcome,
            });
          }
        },
        onError: (err) =>
          setDecisionError({
            requestId,
            message: decisionErrorMessage(err, describeError, t, 'admin.requests.approveError'),
          }),
      },
    );
  };
  // Open the decline confirmation for a row (from the list card or the review
  // modal). The mutation fires only once the leader confirms.
  const handleDecline = (requestId: string) => {
    const row = rows.find((r) => r.id === requestId) ?? null;
    if (row === null) return;
    setDeclineTarget(row);
  };
  const confirmDecline = (row: MembershipRequestView) => {
    setDecisionError(null);
    decline.mutate(
      { groupId, requestId: row.id, kind },
      {
        onSuccess: () =>
          trackIntakeRequestDecided({
            // undefined applicantType = a legacy user-subject request.
            applicant_type: row.applicantType ?? 'user',
            destination_type: kind,
            outcome: 'declined',
          }),
        onError: (err) =>
          setDecisionError({
            requestId: row.id,
            message: decisionErrorMessage(err, describeError, t, 'admin.requests.declineError'),
          }),
      },
    );
  };

  const applicantNameOf = (r: MembershipRequestView): string => {
    if (r.applicantType === 'circle') {
      return r.subjectCircle?.name ?? t('admin.requests.unknownName');
    }
    const full = `${r.subject.firstName ?? ''} ${r.subject.lastName ?? ''}`.trim();
    return full.length > 0 ? full : t('admin.requests.unknownName');
  };

  // Entity-aware "view" navigation, shared by the request card and the review
  // modal so the destination logic lives in exactly one place.
  const viewProfile = (userId: string) =>
    navigate({ to: '/members/$id', params: { id: userId } });
  const viewCircle = (circleId: string) =>
    navigate({ to: '/circles/$id', params: { id: circleId } });

  const tabs: TabBarItem[] = [
    { id: 'pending', label: t('admin.requests.tabAwaitingReview'), count: pending.requests.length },
    { id: 'accepted', label: t('admin.requests.tabAccepted'), count: accepted.requests.length },
    { id: 'declined', label: t('admin.requests.tabDeclined'), count: declined.requests.length },
  ];

  const showSkeleton = useDelayedLoading(isLoading);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('admin.requests.title')}
        subtitle={t('admin.requests.subtitle')}
      />
      <TabBar items={tabs} value={tab} onChange={(id) => setTab(id as ReviewTab)} />

      {messageError !== null ? <InlineStatus>{messageError}</InlineStatus> : null}

      {showSkeleton ? (
        <>
          <PageHeaderSkeleton />
          <AdminJoinRequestsCardSkeleton rows={3} />
        </>
      ) : isLoading ? null : (
        <AdminJoinRequestsCard
          requests={rows}
          status={tab}
          onApprove={handleApprove}
          onDecline={handleDecline}
          onMessage={messageMember}
          onView={setViewing}
          onViewProfile={viewProfile}
          onViewCircle={viewCircle}
          isPending={isPending}
          decisionError={decisionError}
        />
      )}

      <ViewApplicationModal
        request={viewing}
        onClose={() => setViewing(null)}
        isPending={isPending}
        onApprove={(requestId) => handleApprove(requestId, () => setViewing(null))}
        error={
          viewing !== null && decisionError?.requestId === viewing.id
            ? decisionError.message
            : null
        }
        onDecline={(requestId) => {
          handleDecline(requestId);
          setViewing(null);
        }}
        onMessage={messageMember}
        onViewProfile={viewProfile}
        onViewCircle={viewCircle}
      />

      <AlertDialog
        open={declineTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeclineTarget(null);
        }}
        title={
          declineTarget !== null
            ? t('joinRequests.declineDialogTitle', {
                name: applicantNameOf(declineTarget),
              })
            : t('joinRequests.declineDialogTitleFallback')
        }
        description={
          declineTarget !== null
            ? t('joinRequests.declineDialogDescription', {
                name: applicantNameOf(declineTarget),
              })
            : ''
        }
        actionLabel={t('joinRequests.declineDialogConfirm')}
        cancelLabel={t('joinRequests.declineDialogCancel')}
        destructive
        onAction={() => {
          if (declineTarget !== null) {
            const target = declineTarget;
            setDeclineTarget(null);
            confirmDecline(target);
          }
        }}
      />
    </Stack>
  );
}
